import androidx.compose.runtime.*
import io.ktor.client.call.*
import io.ktor.client.request.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

@Serializable
data class TourContentPageDto(
    val id: String, val slug: String, val title: String,
    @SerialName("body_html") val bodyHtml: String = "",
    @SerialName("menu_order") val menuOrder: Int = 100,
    @SerialName("is_published") val isPublished: Boolean = false,
    @SerialName("updated_at") val updatedAt: String = "",
)

@Serializable
private data class TourContentInput(
    val slug: String, val title: String,
    @SerialName("body_html") val bodyHtml: String,
    @SerialName("menu_order") val menuOrder: Int,
    @SerialName("is_published") val isPublished: Boolean,
)

private object TourContentApi {
    suspend fun pages(token: String?) = TourApi.rows<TourContentPageDto>("tour_content_page", token, "menu_order,title,id",
        columns = "id,slug,title,menu_order,is_published,updated_at")
    suspend fun page(token: String?, slug: String) = TourApi.rows<TourContentPageDto>("tour_content_page", token, "id", mapOf("slug" to "eq.$slug")).singleOrNull()
    suspend fun save(token: String, old: TourContentPageDto?, input: TourContentInput) {
        val result: List<TourContentPageDto> = if (old == null) httpClient.post("$SUPABASE_URL/rest/v1/tour_content_page") {
            tourAuth(token); header("Prefer", "return=representation"); setBody(input)
        }.body() else httpClient.patch("$SUPABASE_URL/rest/v1/tour_content_page") {
            tourAuth(token); header("Prefer", "return=representation"); parameter("id", "eq.${old.id}")
            parameter("updated_at", "eq.${old.updatedAt}"); setBody(input)
        }.body()
        check(result.size == 1) { "Stránka se mezitím změnila nebo chybí oprávnění. Načtěte aktuální verzi." }
    }
}

@Composable
fun TourContentNavigation(section: String, navigate: (String) -> Unit) {
    var pages by remember { mutableStateOf<List<TourContentPageDto>>(emptyList()) }
    LaunchedEffect(section) {
        try { pages = TourContentApi.pages(null) }
        catch (e: Throwable) { if (e is CancellationException) throw e }
    }
    if (pages.none { it.slug == "propozice" }) NavButton("Propozice", section in listOf("propozice", "stranka/propozice")) { navigate("stranka/propozice") }
    pages.forEach { p -> NavButton(p.title, section == "stranka/${p.slug}" || section == p.slug) { navigate("stranka/${p.slug}") } }
}

@Composable
fun TourPublicContent(slug: String) {
    var page by remember(slug) { mutableStateOf<TourContentPageDto?>(null) }
    var error by remember(slug) { mutableStateOf<String?>(null) }
    var loading by remember(slug) { mutableStateOf(true) }
    var retry by remember { mutableStateOf(0) }
    LaunchedEffect(slug, retry) {
        loading = true; error = null; page = null
        try { page = TourContentApi.page(null, slug) }
        catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { loading = false }
    }
    Div(attrs = { classes("card") }) {
        H2 { Text(page?.title ?: if (slug == "propozice") "Propozice" else "Obsahová stránka") }
        if (loading) P { Text("Načítám…") }
        TourError(error)
        if (error != null) Button(attrs = { onClick { retry++ } }) { Text("Načíst znovu") }
        if (!loading && error == null && page == null) P { Text("Stránka zatím nebyla zveřejněna.") }
        page?.let { RichContent(it.bodyHtml) }
    }
}

@Composable
fun TourContentAdmin(token: String) {
    var pages by remember { mutableStateOf<List<TourContentPageDto>>(emptyList()) }
    var selected by remember { mutableStateOf<String?>(null) }
    var editing by remember { mutableStateOf<TourContentPageDto?>(null) }
    var error by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    var refresh by remember { mutableStateOf(0) }
    LaunchedEffect(refresh) {
        loading = true; error = null
        try { pages = TourContentApi.pages(token) }
        catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
        finally { loading = false }
    }
    LaunchedEffect(selected, refresh) {
        editing = null
        if (selected != null && selected != "") {
            loading = true
            try {
                editing = TourContentApi.page(token, selected!!)
                check(editing != null) { "Stránka nebyla nalezena. Načtěte seznam znovu." }
            } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
            finally { loading = false }
        }
    }
    Div(attrs = { classes("card") }) {
        H2 { Text("Obsahové stránky Tour") }
        P { Text("Stránky mají vlastní obsah Tour. Zveřejněné stránky se zobrazí v navigaci.") }
        TourError(error)
        if (loading) P { Text("Načítám…") }
        Button(attrs = { classes("primary"); if (loading) disabled(); onClick { selected = ""; editing = null } }) { Text("Nová obsahová stránka") }
        Button(attrs = { onClick { refresh++ } }) { Text("Načíst stránky znovu") }
        Table {
            Tr { Th { Text("Stránka") }; Th { Text("Stav") }; Th { Text("Pořadí v menu") }; Th {} }
            pages.forEach { p -> Tr {
                Td { Text(p.title) }; Td { Text(if (p.isPublished) "Zveřejněná" else "Koncept") }; Td { Text(p.menuOrder.toString()) }
                Td { Button(attrs = { if (loading) disabled(); onClick { selected = p.slug } }) { Text("Upravit ${p.title}") } }
            } }
        }
    }
    if (selected == "" || editing != null) key(selected, editing?.updatedAt) {
        TourContentForm(token, editing, onSaved = { selected = null; editing = null; refresh++ }, onCancel = { selected = null; editing = null })
    }
}

@Composable
private fun TourContentForm(token: String, original: TourContentPageDto?, onSaved: () -> Unit, onCancel: () -> Unit) {
    var title by remember { mutableStateOf(original?.title ?: "") }
    var slug by remember { mutableStateOf(original?.slug ?: "") }
    var body by remember { mutableStateOf(original?.bodyHtml ?: "") }
    var order by remember { mutableStateOf((original?.menuOrder ?: 100).toString()) }
    var published by remember { mutableStateOf(original?.isPublished ?: false) }
    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    Div(attrs = { classes("card") }) {
        H2 { Text(if (original == null) "Nová stránka Tour" else "Upravit stránku: ${original.title}") }
        TourError(error)
        Form(attrs = { onSubmit {
            it.preventDefault()
            if (!busy) scope.launch {
                busy = true; error = null
                try {
                    val menuOrder = order.toIntOrNull() ?: error("Pořadí musí být celé číslo.")
                    TourContentApi.save(token, original, TourContentInput(slug.trim(), title.trim(), sanitizeHtml(body), menuOrder, published)); onSaved()
                } catch (e: Throwable) { if (e is CancellationException) throw e; error = e.apiMessage() }
                finally { busy = false }
            }
        } }) {
            Fieldset(attrs = { if (busy) attr("disabled", "") }) {
                Div(attrs = { classes("tour-form") }) {
                    TourField("Název obsahové stránky", title) { title = it }
                    TourField("URL stránky", slug) { slug = it }
                    P { Text("Malá písmena bez diakritiky, číslice a spojovníky. Například propozice.") }
                    TourField("Pořadí stránky v menu", order) { order = it }
                    Label {
                        Input(type = InputType.Checkbox) { attr("aria-label", "Zveřejnit stránku"); checked(published); onChange { published = it.value } }
                        Text("Zveřejnit stránku")
                    }
                }
                ContentEditor(token, sanitizeHtml(original?.bodyHtml ?: ""), imageBucket = "tour-content-images") { body = it }
                Button(attrs = { type(ButtonType.Submit) }) { Text("Uložit obsahovou stránku") }
                Button(attrs = { type(ButtonType.Button); onClick { onCancel() } }) { Text("Zrušit úpravu stránky") }
            }
        }
        TourAdminDisclosure("Náhled stránky") {
            RichContent(body)
        }
    }
}
